// What the two clip editors share — the audio clip editor (ClipEditor.tsx)
// and the piano roll (piano-roll/PianoRoll.tsx) — so a clip opens into the
// same frame whichever kind it is: the clip's strip along the top (colour,
// name, what it is, its actions, play / undo / redo / fold / close), tabs in
// the view switcher's style (Pads · Arrange · Mix), and one set of controls
// under them. Every control is a thumb's 44px on a phone and the room's
// 28–32px on a wide screen; an active choice reads like the view switcher's
// active tab, so the editor looks like the rest of the room rather than a
// page of its own.

import { useEffect, useState, type ReactNode } from "react";
import { ChevronDown, Download, Minus, Play, Plus, Redo2, Square, Undo2, X } from "lucide-react";
import { RenderStatus } from "@web-shared/components/RenderStatus";
import { cn } from "@web-shared/lib/utils";

/** A short buzz on a phone, where a press otherwise has no feel. */
export function buzz(ms = 8): void {
  try {
    navigator.vibrate?.(ms);
  } catch {
    // Some browsers throw before the page has been interacted with.
  }
}

/** The view switcher's active tab (RoomPage's ViewSwitch), for any choice. */
export const ACTIVE = "bg-controlline text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]";
/** A choice that isn't: a quiet pad that lights on hover. */
export const IDLE = "bg-white/5 text-foreground/80 hover:bg-white/10";

/** Live's clip colours (its palette's bright row), as Live stores them. */
export const CLIP_COLORS: readonly number[] = [
  0xff94a6, 0xffa529, 0xcc9927, 0xf7f47c, 0xbffb00, 0x1aff2f, 0x25ffa8, 0x5cffe8, 0x8bc5ff, 0x5480e4, 0x92a7ff,
  0xd86ce4, 0xe553a0,
];
export const hex = (rgb: number): string => `#${(rgb & 0xffffff).toString(16).padStart(6, "0")}`;

/** A clip's length the way the timeline's footer says it: bars, or beats under one. */
export function lengthLabel(beats: number, beatsPerBar = 4): string {
  const bars = beats / beatsPerBar;
  if (bars >= 1 && Math.abs(bars - Math.round(bars)) < 1e-6) return `${Math.round(bars)} bar${Math.round(bars) === 1 ? "" : "s"}`;
  if (bars >= 1) return `${Math.round(bars * 10) / 10} bars`;
  const b = Math.round(beats * 100) / 100;
  return `${b} beat${b === 1 ? "" : "s"}`;
}

// ---------------------------------------------------------------------------
// The frame
// ---------------------------------------------------------------------------

export interface EditorHistory {
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * The clip's strip, docked: the same row the minimized editor shows (the
 * clip's colour washing in from the left, its name, what it is), so opening
 * it grows the strip rather than swapping it for a different header. The
 * clip's actions (copy, split, delete…) ride in it while it is open.
 */
export function EditorHeader({
  color,
  name,
  meta,
  muted,
  render,
  actions,
  onPreview,
  previewing = false,
  history,
  onDownload,
  onMinimize,
  onClose,
}: {
  color: string;
  name: string;
  /** "4 bars · 32 notes", "4 bars · warped at 92 BPM". */
  meta?: ReactNode;
  muted?: boolean;
  /** Where the clip's render for Live and Audiotool stands (absent: none). */
  render?: "pending" | "ready";
  /** The clip's action bar (RoomPage's ClipActionBar), between the name and the transport. */
  actions?: ReactNode;
  onPreview?: () => void;
  previewing?: boolean;
  history?: EditorHistory;
  onDownload?: () => void;
  onMinimize?: () => void;
  onClose: () => void;
}) {
  return (
    <div
      className="flex h-10 shrink-0 select-none items-center gap-1 border-b border-panelline px-3 text-xs"
      style={{ background: `linear-gradient(90deg, ${color}26 0%, var(--sunk, transparent) 320px)` }}
    >
      <span className="size-3 shrink-0 rounded-sm" style={{ backgroundColor: color }} />
      <h2 className="min-w-[3rem] shrink truncate pl-1 text-sm font-semibold" title={name}>
        {name || "Clip"}
      </h2>
      <RenderStatus status={render} className="text-muted-foreground" />
      {muted && <span className="shrink-0 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">muted</span>}
      {meta && <span className="min-w-0 shrink-[4] truncate text-muted-foreground">{meta}</span>}
      <div className="ml-auto flex min-w-0 items-center gap-1">
        {actions && <div className="flex min-w-0 items-center border-r border-panelline pr-2">{actions}</div>}
        {onPreview && (
          <IconButton label={previewing ? "Stop — everything comes back" : "Hear this clip alone, in time (everything else ducks until you stop)"} onClick={onPreview} active={previewing}>
            {previewing ? <Square /> : <Play />}
          </IconButton>
        )}
        {history && (
          <>
            <IconButton label="Undo" onClick={history.undo} disabled={!history.canUndo}>
              <Undo2 />
            </IconButton>
            <IconButton label="Redo" onClick={history.redo} disabled={!history.canRedo}>
              <Redo2 />
            </IconButton>
          </>
        )}
        {onDownload && (
          <IconButton label="Download this clip" onClick={onDownload}>
            <Download />
          </IconButton>
        )}
        {onMinimize && (
          <IconButton label="Minimize the clip editor" onClick={onMinimize}>
            <ChevronDown />
          </IconButton>
        )}
        <IconButton label="Close clip editor" onClick={onClose}>
          <X />
        </IconButton>
      </div>
    </div>
  );
}

/** The editor's tabs, in the view switcher's style. */
export function EditorTabs<T extends string>({
  tabs,
  value,
  onChange,
  column,
  label,
}: {
  tabs: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  /** A phone's sheet: thumb-height tabs. */
  column: boolean;
  label: string;
}) {
  return (
    <div role="tablist" aria-label={label} className="grid shrink-0 grid-flow-col auto-cols-fr gap-1 px-2 py-1.5">
      {tabs.map((t) => (
        <button
          key={t.value}
          type="button"
          role="tab"
          aria-selected={value === t.value}
          onClick={() => {
            buzz(4);
            onChange(t.value);
          }}
          className={cn(
            "rounded-lg font-semibold transition-colors",
            column ? "h-10 text-sm" : "h-8 text-xs",
            value === t.value ? ACTIVE : "text-muted-foreground hover:text-foreground/80",
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The Clip tab: the same in both editors
// ---------------------------------------------------------------------------

export function ClipPanel({
  name,
  color,
  muted,
  canBounce = false,
  onName,
  onColor,
  onMuted,
  onBounce,
}: {
  name: string;
  color: number;
  muted: boolean;
  canBounce?: boolean;
  onName: (name: string) => void;
  onColor: (color: number) => void;
  onMuted: (muted: boolean) => void;
  onBounce?: () => Promise<string>;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [bounce, setBounce] = useState<{ busy: boolean; note: string | null; error: boolean }>({ busy: false, note: null, error: false });
  const commitName = () => {
    if (draft === null) return;
    const next = draft.trim();
    setDraft(null);
    if (next && next !== name) onName(next);
  };
  const current = color & 0xffffff;
  return (
    <div className="flex flex-col gap-4">
      <Field label="Name">
        <input
          type="text"
          value={draft ?? name}
          enterKeyHint="done"
          aria-label="Clip name"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") {
              setDraft(null);
              e.currentTarget.blur();
            }
          }}
          className="h-11 rounded-lg bg-panel px-3 text-sm font-semibold outline-none ring-1 ring-inset ring-panelline focus:ring-white/30 wide:h-8 wide:text-xs"
        />
      </Field>
      {/* Live's swatches: square, in one wrapping row, the clip's own ringed. */}
      <Field label="Colour">
        <div className="flex flex-wrap gap-1.5 wide:gap-1">
          {CLIP_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Colour ${hex(c)}`}
              aria-pressed={c === current}
              onClick={() => {
                if (c !== current) onColor(c);
              }}
              className={cn(
                "size-8 rounded-md transition-transform active:scale-90 wide:size-5 wide:rounded-sm",
                c === current ? "ring-2 ring-white ring-offset-2 ring-offset-sunk" : "hover:scale-110",
              )}
              style={{ backgroundColor: hex(c) }}
            />
          ))}
        </div>
      </Field>
      <Field label="Playback">
        <BigChoice<"on" | "muted">
          value={muted ? "muted" : "on"}
          onChange={(v) => onMuted(v === "muted")}
          options={[
            { value: "on", label: "Plays" },
            { value: "muted", label: "Muted" },
          ]}
        />
      </Field>
      {onBounce && (
        <Field
          label="Bounce"
          hint="Replaces this clip's audio with what you hear (warp, pitch and loop). The original is kept as a backup in the Session View's first empty row."
        >
          <button
            type="button"
            disabled={bounce.busy || !canBounce}
            onClick={() => {
              setBounce({ busy: true, note: null, error: false });
              onBounce()
                .then((note) => setBounce({ busy: false, note, error: false }))
                .catch((err: unknown) =>
                  setBounce({ busy: false, note: err instanceof Error ? err.message : "Couldn't bounce this clip.", error: true }),
                );
            }}
            className={cn("h-12 rounded-xl px-4 text-sm font-semibold disabled:opacity-50 wide:h-8 wide:text-xs", IDLE)}
          >
            {bounce.busy ? "Bouncing…" : "Bounce in place"}
          </button>
          {bounce.note && (
            <p role="status" className={cn("mt-1.5 text-[11px] leading-snug", bounce.error ? "text-danger" : "text-[var(--changed)]")}>
              {bounce.note}
            </p>
          )}
        </Field>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Controls — every one at least 44px on a phone.
// ---------------------------------------------------------------------------

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
      {children}
      {hint && <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** Big, equal choices — a row of pads rather than a radio list; `columns`
 * wraps them into rows (the default is one row). */
export function BigChoice<T extends string>({
  value,
  onChange,
  options,
  columns,
  label,
}: {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ value: T; label: string; title?: string }>;
  columns?: number;
  label?: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${columns ?? options.length}, minmax(0, 1fr))` }}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          title={o.title}
          onClick={() => {
            if (o.value !== value) onChange(o.value);
          }}
          className={cn(
            "h-12 rounded-xl px-2 text-sm font-semibold whitespace-nowrap transition-transform active:scale-95 wide:h-8 wide:rounded-lg wide:text-xs",
            value === o.value ? ACTIVE : IDLE,
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A row of actions, each one pad: what a menu would hold, out where a thumb is. */
export function PadRow({ children, cols }: { children: ReactNode; cols?: number }) {
  return (
    <div
      className={cn("grid gap-1.5", !cols && "grid-cols-[repeat(auto-fill,minmax(5.5rem,1fr))] wide:grid-cols-[repeat(auto-fill,minmax(4.5rem,1fr))]")}
      style={cols ? { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` } : undefined}
    >
      {children}
    </div>
  );
}

/** One action pad (or a toggle, with `active`). */
export function Pad({
  children,
  label,
  onClick,
  disabled,
  active,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        buzz(6);
        onClick();
      }}
      disabled={disabled}
      title={label}
      aria-pressed={active}
      className={cn(
        "flex h-12 items-center justify-center gap-1.5 rounded-xl px-2 text-sm font-semibold whitespace-nowrap transition-transform active:scale-95 disabled:opacity-30 wide:h-8 wide:rounded-lg wide:text-xs [&_svg]:size-4 wide:[&_svg]:size-3.5",
        active ? ACTIVE : IDLE,
      )}
    >
      {children}
    </button>
  );
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ value: T; label: string; title?: string }>;
}) {
  return (
    <div role="radiogroup" className="inline-flex shrink-0 rounded-lg border border-panelline bg-panel p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          title={o.title}
          onClick={() => {
            if (o.value !== value) onChange(o.value);
          }}
          className={cn(
            "h-11 rounded-md px-3 text-xs font-semibold whitespace-nowrap wide:h-7 wide:px-2.5",
            value === o.value ? ACTIVE : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function IconButton({
  children,
  label,
  onClick,
  disabled,
  active,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        "flex size-11 shrink-0 items-center justify-center rounded-lg wide:size-8 [&_svg]:size-5 wide:[&_svg]:size-4",
        active ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-white/5",
        "disabled:opacity-30",
      )}
    >
      {children}
    </button>
  );
}

export function ToolButton({
  children,
  label,
  onClick,
  disabled,
  active,
  danger,
  iconOnly,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  danger?: boolean;
  iconOnly?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={cn(
        "flex h-11 shrink-0 items-center justify-center gap-1 rounded-lg border text-xs font-semibold wide:h-7 [&_svg]:size-4 wide:[&_svg]:size-3.5",
        iconOnly ? "w-11 wide:w-7" : "px-3 wide:px-2",
        active ? cn("border-transparent", ACTIVE) : "border-panelline text-foreground/80 hover:bg-white/5",
        danger && "text-danger",
        "disabled:opacity-30",
      )}
    >
      {children}
    </button>
  );
}

export function TextButton({
  children,
  label,
  onClick,
  disabled,
  className,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        "h-11 shrink-0 rounded-lg border border-panelline px-3 text-xs font-semibold text-foreground/80 hover:bg-white/5 disabled:opacity-30 wide:h-7 wide:px-2",
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * − value + : a number a thumb can change a step at a time, or tap to type.
 * Commits on each step, and on Enter or blur when typed — never per keystroke.
 */
export function Stepper({
  value,
  step,
  min,
  max,
  unit,
  prefix,
  label,
  decimals = 0,
  signed = false,
  disabled,
  onCommit,
}: {
  value: number;
  step: number;
  min: number;
  max: number;
  unit?: string;
  prefix?: string;
  label: string;
  decimals?: number;
  signed?: boolean;
  disabled?: boolean;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => setDraft(null), [value]);
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  const shown = (() => {
    const r = Math.round(value * 10 ** decimals) / 10 ** decimals;
    return signed && r > 0 ? `+${r}` : String(r);
  })();
  const commit = () => {
    if (draft === null) return;
    const v = Number(draft.replace(/^\+/, ""));
    setDraft(null);
    if (!Number.isFinite(v) || Math.abs(v - value) < 1e-6) return;
    onCommit(clamp(v));
  };
  const nudge = (dir: 1 | -1) => {
    const raw = value + dir * step;
    const next = clamp(decimals === 0 ? Math.round(raw) : Math.round(raw * 100) / 100);
    if (Math.abs(next - value) > 1e-9) onCommit(next);
  };
  return (
    <div className={cn("inline-flex h-11 shrink-0 items-center rounded-lg bg-panel ring-1 ring-inset ring-panelline wide:h-7", disabled && "opacity-40")}>
      <button
        type="button"
        disabled={disabled || value <= min}
        onClick={() => nudge(-1)}
        aria-label={`${label}: down`}
        className="flex h-full w-11 items-center justify-center text-foreground/80 hover:bg-white/5 disabled:opacity-30 wide:w-7 [&_svg]:size-4 wide:[&_svg]:size-3.5"
      >
        <Minus />
      </button>
      <label className="flex h-full items-center gap-1 px-1">
        {prefix && <span className="text-muted-foreground">{prefix}</span>}
        <input
          type="text"
          inputMode="decimal"
          enterKeyHint="done"
          disabled={disabled}
          value={draft ?? shown}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") {
              setDraft(null);
              e.currentTarget.blur();
            }
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
              e.preventDefault();
              nudge(e.key === "ArrowUp" ? 1 : -1);
            }
          }}
          aria-label={label}
          className="tabular w-[3.25rem] bg-transparent text-center text-sm font-semibold outline-none wide:w-12 wide:text-xs"
        />
        {unit && <span className="text-muted-foreground">{unit}</span>}
      </label>
      <button
        type="button"
        disabled={disabled || value >= max}
        onClick={() => nudge(1)}
        aria-label={`${label}: up`}
        className="flex h-full w-11 items-center justify-center text-foreground/80 hover:bg-white/5 disabled:opacity-30 wide:w-7 [&_svg]:size-4 wide:[&_svg]:size-3.5"
      >
        <Plus />
      </button>
    </div>
  );
}
